import { CommonModule } from '@angular/common';
import { Component, Inject, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { ChannelMatchingMode, PageQuery } from '@app/common/page-query';
import { Roles } from '@app/common/roles';
import { PageFilterModule, TopNewsOnlyOption } from '@app/components/page-filter/page-filter.component';
import { PageListModule } from '@app/components/page-list/page-list.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { Page, PageType } from '@app/models/page';
import { PeoplePickerUser } from '@app/models/people-picker-user';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { ChannelService } from '@app/services/channel.service';
import { PageCenterService } from '@app/services/page-center.service';
import { PagesService } from '@app/services/pages.service';
import { SecurityService } from '@app/services/security.service';
import { UserService } from '@app/services/user.service';
import { Environment } from '@environments/interface.environment';
import { User } from '@microsoft/microsoft-graph-types';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-page-center',
  templateUrl: './page-center.component.html',
  styleUrls: ['./page-center.component.scss']
})
export class PageCenterComponent implements OnInit, OnDestroy {

  @Input() pageType: PageType;
  @Input() editorOptionsEnabled: boolean;
  @Input() filterChannelGuid: string;

  amountOfPagesToDisplay = 5;
  fetchedAmountOfPages = 0;
  amountOfNewsToIncreaseWith = 5;
  amountOfPagesToDisplayCurrently = this.amountOfNewsToIncreaseWith;
  amountOfPagesMatchingFilter = 0;

  lightEnabled: boolean;
  allowedToCreateNew$: Observable<boolean>;

  allPagesFromQuery$: Observable<Array<Page>>;
  filteredPages$: Observable<Array<Page>>;
  channels$: Observable<Array<SimplifiedTerm>>;
  userInfo$: Observable<User>;

  morePagesSubject: BehaviorSubject<number>;
  reloadPagesSubject: BehaviorSubject<any>;
  queryOptionsChanged$: Subject<any>;
  channelFilterChanged$: Subject<{
    selectedChannels: Array<SimplifiedTerm>,
    channelsMatchingOption: ChannelMatchingMode
  }>;
  selectedOwner$: Observable<PeoplePickerUser>;

  loading = true;
  selectedChannelsNameMappings = new Map<string, string>();

  deleteSubscription: Subscription;

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private channelService: ChannelService,
              private pagesService: PagesService,
              private pageCenterService: PageCenterService,
              private securityService: SecurityService,
              private userService: UserService) {
  }

  ngOnInit() {
    this.lightEnabled = this.environment.enableWorkplaceLight;
    this.allowedToCreateNew$ = this.securityService.hasUserAnyAppRole([Roles.Admin, Roles.Editor]);
    this.userInfo$ = this.userService.getMyDetailsFromMsGraph();
    this.morePagesSubject = new BehaviorSubject<number>(this.amountOfPagesToDisplay);
    this.reloadPagesSubject = new BehaviorSubject<any>(null);
    this.channelFilterChanged$ = new BehaviorSubject<any>({
      selectedChannels: (this.filterChannelGuid && [{ Guid: this.filterChannelGuid }]) || [],
      channelsMatchingOption: ChannelMatchingMode.Or
    });
    this.queryOptionsChanged$ = new Subject<any>();

    this.allPagesFromQuery$ = combineLatest([
      this.queryOptionsChanged$,
      this.userInfo$,
      this.reloadPagesSubject
    ]).pipe(
      tap(() => this.loading = true),
      switchMap(([queryOptions, user]) => {
        const query: PageQuery = {
          PageType: this.pageType,
          FindText: queryOptions.searchText,
          StartDate: queryOptions.startDate,
          EndDate: queryOptions.endDate,
          OwnerOrEditorUpnId: queryOptions.OwnerOrEditorUpnId,
          Featured: this.pageType === PageType.Content ? null : queryOptions.topNewsOnlyOption === TopNewsOnlyOption.TopNewsOnly ? true : null,
          IncludeDrafts: queryOptions.includeDrafts,
          Channels: null,
          ChannelMatchingMode: null,
          IncludeArchive: queryOptions.includeArchive
        };
        if (queryOptions.onlyCurrentUserPages) {
          query.OwnerOrEditorUpnId = user.userPrincipalName;
        }
        // Override language filtering functionality if "onlyInCurrentUserLanguages" option is false.
        // Note that this has no effect if the language filter isn't enabled/configured for the environment to begin with. And the option shouldn't show either.
        const overrideLanguageFilter = queryOptions.onlyInCurrentUserLanguages ? false : true;
        return this.pageCenterService.getAllPagesByQuery(query, overrideLanguageFilter);
      }),
      tap(allPagesFromQuery => {
        this.loading = false;
        this.fetchedAmountOfPages = allPagesFromQuery.length;
      }),
      startWith([]),
      shareReplay());

    this.filteredPages$ = combineLatest([
      this.allPagesFromQuery$,
      this.channelFilterChanged$,
      this.morePagesSubject]).pipe(
        map(([pages, channelFilterValues]) => {
          if (channelFilterValues.selectedChannels.length === 0) {
            // Send back all pages if no channels are selected
            return pages;
          } else if (channelFilterValues.channelsMatchingOption === ChannelMatchingMode.Or) {
            return pages.filter(p => channelFilterValues.selectedChannels.some(c => p.ChannelTermPages.some(ctp => ctp.ChannelTermId === c.Guid)));
          } else if (channelFilterValues.channelsMatchingOption === ChannelMatchingMode.And) {
            return pages.filter(p => channelFilterValues.selectedChannels.every(c => p.ChannelTermPages.some(ctp => ctp.ChannelTermId === c.Guid)));
          }
        }),
        map(filteredPages => {
          // Sort the returned pages based on publishing date
          return filteredPages.sort((a, b) => {
            if (a.PublishingDate === null && b.PublishingDate === null) {
              return 0;
            }
            if (a.PublishingDate === null) {
              return -1; // Sort null values to the top, i.e drafts should show first
            }
            if (b.PublishingDate === null) {
              return 1; // Sort null values to the top, i.e drafts should show first
            }
            // If both items have a date specified, sort the most recent one on top
            return new Date(b.PublishingDate).getTime() - new Date(a.PublishingDate).getTime();
          });
        }),
        map((sortedAndfilteredPages) => {
          // If the filtering and sorted results in less amount of news showing than the current amount to display, reset the display counter
          this.amountOfPagesMatchingFilter = sortedAndfilteredPages.length;
          if (this.amountOfPagesMatchingFilter < this.amountOfPagesToDisplay) {
            this.amountOfPagesToDisplayCurrently = this.amountOfPagesToDisplay;
          }
          return sortedAndfilteredPages.slice(0, this.amountOfPagesToDisplayCurrently);
        }),
        shareReplay());

    this.channels$ = combineLatest([
      this.channelService.getChannels(),
      this.allPagesFromQuery$,
      this.channelFilterChanged$
    ])
      .pipe(
        map(([channels, allPagesFromQuery, channelFilter]) => {

          // Get the available channels from the fetched news
          const availableChannels: string[] = [...new Set(allPagesFromQuery.map(n => n.ChannelTermPages.map(ctp => ctp.ChannelTermId)).reduce((acc, val) => acc.concat(val), []))];
          // Modify channels for selection (recursively)
          channels.forEach(cn => {
            this.modifyChannelForSelection(cn, allPagesFromQuery, availableChannels, channelFilter);
          });

          return channels;
        }));

  }

  modifyChannelForSelection(channel: SimplifiedTerm, allPages: Page[], availableChannels: string[], channelFilter: { selectedChannels: Array<SimplifiedTerm>, channelsMatchingOption: ChannelMatchingMode }) {
    if (!this.selectedChannelsNameMappings.has(channel.Guid)) {
      this.selectedChannelsNameMappings.set(channel.Guid, channel.Name);
    }

    let pagesHavingTerm = 0;
    const isChannelSelected = channel.Selected || channelFilter.selectedChannels.some(sc => sc.Guid === channel.Guid);
    // Edge case: never automatically disable channels which are already selected by the user; prevents "lockout" from the filter
    if (!availableChannels.some(ac => ac === channel.Guid) && !isChannelSelected) {
      channel.IsAvailableForTagging = false;
    } else if (availableChannels.some(ac => ac === channel.Guid) && !isChannelSelected) {
      // Channel isn't selected
      if (channelFilter.channelsMatchingOption === ChannelMatchingMode.Or) {
        // Any option
        pagesHavingTerm = allPages.map(n => n.ChannelTermPages.filter(ctp => ctp.ChannelTermId === channel.Guid)).reduce((acc, val) => acc.concat(val), []).length;
      } else if (channelFilter.channelsMatchingOption === ChannelMatchingMode.And) {
        // All option
        pagesHavingTerm = allPages.filter(p => {
          return p.ChannelTermPages.some(ctp => ctp.ChannelTermId === channel.Guid) && channelFilter.selectedChannels.every(sc => p.ChannelTermPages.some(ctp => ctp.ChannelTermId === sc.Guid));
        }).length;
      }
    } else if (isChannelSelected) {
      // Channel is selected
      channel.Selected = true;
    }
    if (pagesHavingTerm > 0) {
      channel.IsAvailableForTagging = true;
      channel.Name = `${this.selectedChannelsNameMappings.get(channel.Guid)} (${pagesHavingTerm})`;
    } else {
      channel.IsAvailableForTagging = false;
      channel.Name = this.selectedChannelsNameMappings.get(channel.Guid);
    }
    channel.Terms?.forEach(c => {
      this.modifyChannelForSelection(c, allPages, availableChannels, channelFilter);
    });
  }

  showMorePages() {
    this.amountOfPagesToDisplayCurrently = this.amountOfPagesToDisplayCurrently + this.amountOfNewsToIncreaseWith;
    this.morePagesSubject.next(this.amountOfPagesToDisplayCurrently + 1);
  }

  showLessPages() {
    this.amountOfPagesToDisplayCurrently = this.amountOfPagesToDisplay;
    this.morePagesSubject.next(this.amountOfPagesToDisplayCurrently + 1);
  }

  deletePage(page: Page) {
    this.deleteSubscription = this.pagesService.deletePage(page.Id)
      .subscribe(result => {
        if (result) {
          this.reloadPagesSubject.next(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.deleteSubscription && this.deleteSubscription.unsubscribe();
  }

}
@NgModule({
  declarations: [
    PageCenterComponent
  ],
  exports: [
    PageCenterComponent
  ],
  imports: [
    WidgetPlaceholderModule,
    CommonModule,
    PageListModule,
    PageFilterModule,
    MatButtonModule
  ]
})
export class PageCenterModule { }
